// Shared test scaffolding for the curation review suites (not a test file).

import React from 'react';
import { render } from '@testing-library/react';
import { CurationReviewProvider } from './state/CurationReviewContext';
import { reviewUrls, ServerReviewState } from './utils/curationApi';
import { AiPayload } from './utils/curationScoring';

export const aiPayload = (overrides: Partial<AiPayload> = {}): AiPayload => ({
  experience: {
    context: {
      factors: [
        { id: 'industry', matched: true, note: 'fintech background' },
        { id: 'company_type', matched: true, note: '' },
        { id: 'role', matched: true, note: '' },
        { id: 'tech_stack', matched: false, note: '' },
      ],
      reasoning: 'Similar role in a similar industry.',
    },
    relevant_years: 6,
  },
  flags: [],
  skills: [
    { evidence: '8 years of Ruby', group: 'must_have', name: 'Ruby', state: 'explicit' },
    { evidence: 'Deployed containers', group: 'must_have', name: 'Docker', state: 'inferred' },
    { evidence: '', group: 'nice_to_have', name: 'Kafka', state: 'not_found' },
  ],
  ...overrides,
});

export const serverReviewState = (
  overrides: Partial<ServerReviewState> = {},
): ServerReviewState => ({
  ai: { completedAt: null, error: null, model: null, payload: null, requestedAt: null },
  decision: null,
  gate: {
    checks: [
      {
        detail: { candidate_country: 'PT', list: ['PT', 'ES'] },
        key: 'eligible_locations',
        kind: 'location',
        override: null,
        verdict: 'pass',
      },
      {
        detail: { candidate_level: 5, language: 'en', language_name: 'English', required_level: 3 },
        key: 'language:en',
        kind: 'language',
        override: null,
        verdict: 'pass',
      },
    ],
    decision: 'gate_passed',
  },
  comparison: {
    languages: {
      candidate: [{ code: 'en', label: 'Native or Bilingual proficiency', level: 5, name: 'English' }],
      job: [{ code: 'en', name: 'English' }],
    },
    location: {
      candidate: {
        citizenships: ['Portuguese'],
        city: 'Lisbon',
        country: 'Portugal',
        inEligibleLocation: true,
        timezone: 'Europe/Lisbon',
        utcOffset: 1,
      },
      job: {
        citizenshipRequired: true,
        eligibleLocations: ['Portugal'],
        eligibleTimezones: [],
        excludedLocations: [],
        officeAttendanceCount: 3,
        officeAttendancePeriod: 'week',
        officeLocations: ['Lisbon, Portugal'],
        openToWorldwide: false,
        visaSupport: false,
        workModel: 'hybrid',
      },
    },
  },
  id: 1,
  job: {
    companyName: 'Brightcore',
    experienceMax: 8,
    experienceMin: 6,
    officeLocations: ['Lisbon, Portugal'],
    title: 'Solutions Architect',
    workModel: 'hybrid',
  },
  overrides: { direct: {}, skills: {} },
  rejectionReasons: [
    { label: 'Location mismatch', value: 'candidate_doesnt_live_within_a_commuting_distance' },
    { label: 'Not the right set of skills', value: 'not_enough_skills' },
    { label: 'Other', value: 'other' },
  ],
  scores: {
    category: null,
    computedContextScore: null,
    computedSkillsScore: null,
    computedYearsScore: null,
    contextScore: null,
    experienceScore: null,
    mustHaveAvg: null,
    niceToHaveAvg: null,
    overallScore: null,
    skillsScore: null,
    yearsScore: null,
  },
  status: 'created',
  suggestedRejectionReason: null,
  ...overrides,
});

export const scoredServerState = (
  overrides: Partial<ServerReviewState> = {},
): ServerReviewState =>
  serverReviewState({
    ai: {
      completedAt: '2026-08-20T10:00:00Z',
      error: null,
      model: 'claude-test',
      payload: aiPayload(),
      requestedAt: '2026-08-20T09:59:00Z',
    },
    status: 'scored',
    ...overrides,
  });

export const renderWithProvider = (
  ui: React.ReactElement,
  server: ServerReviewState = serverReviewState(),
) =>
  render(
    <CurationReviewProvider
      experienceMax={server.job.experienceMax}
      experienceMin={server.job.experienceMin}
      finishCurationPath={{ path: '/curators/inbox', same_person: false }}
      initialReview={server}
      urls={reviewUrls(1)}
    >
      {ui}
    </CurationReviewProvider>,
  );

// Jest 27's jsdom has no fetch — install a mock per test and remove it after.
export const mockFetch = (mock?: jest.Mock): jest.Mock => {
  const implementation =
    mock ||
    jest.fn(() =>
      Promise.resolve({ json: () => Promise.resolve({}), ok: true, status: 200 } as Response),
    );
  (window as unknown as { fetch: unknown }).fetch = implementation;
  return implementation;
};

export const removeFetchMock = () => {
  delete (window as unknown as { fetch?: unknown }).fetch;
};
